import { useState, useEffect, useRef } from 'react';
import { Heading, Paragraph, Label, Button } from '@/components/atoms';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/atoms';
import { TwoFactorDialogWrapper } from './two-factor-dialog-wrapper';
import { useSetupTwoFactor, useVerifyTwoFactor } from '@/api';
import { useBusiness } from '@/api/queries/business';

function maskPhoneNumber(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  // Keep leading + if present, and mask the middle digits.
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/[^\d]/g, '');
  if (digits.length <= 4) return trimmed;
  const first = digits.slice(0, 3);
  const last = digits.slice(-2);
  const masked = `${first}${'•'.repeat(Math.max(0, digits.length - 5))}${last}`;
  return hasPlus ? `+${masked}` : masked;
}

interface SMSVerificationProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onSuccess: () => void;
}

export const SMSVerification = ({
  isOpen,
  onClose,
  onBack,
  onSuccess,
}: SMSVerificationProps) => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const { data: business } = useBusiness();
  const whatsappNumber =
    business?.submittedData?.phoneNumber != null
      ? maskPhoneNumber(business.submittedData.phoneNumber)
      : '';

  const { mutate: setupTwoFactor, isPending: isSettingUp } = useSetupTwoFactor(
    () => {
      setCodeSent(true);
      setTimer(60);
      setCanResend(false);
    }
  );

  const { mutate: verifyTwoFactor, isPending: isVerifying } =
    useVerifyTwoFactor(() => {
      onSuccess();
    });

  const initialized = useRef(false);

  useEffect(() => {
    if (timer > 0 && codeSent) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [timer, codeSent]);

  const handleSendCode = () => {
    if (!initialized.current) {
      initialized.current = true;
      setupTwoFactor({ method: 'whatsapp' });
    }
  };

  const handleVerify = () => {
    if (otp.length !== 6) {
      setError('Invalid code. please try again');
      return;
    }
    verifyTwoFactor({ token: otp });
  };

  const handleResend = () => {
    if (canResend) {
      setupTwoFactor({ method: 'whatsapp' });
      setOtp('');
      setError('');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}.${secs.toString().padStart(2, '0')}`;
  };

  return (
    <TwoFactorDialogWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Set up Two-Factor Authentication"
      onBack={onBack}
      onContinue={handleVerify}
      continueLabel="Verify & Continue"
      continueDisabled={!codeSent || otp.length !== 6 || isSettingUp}
      isLoading={isVerifying}
      showBackButton
    >
      <div className="flex flex-col gap-6">
        <div>
          <Heading size="lg" className="text-gray-900 mb-2">
            WhatsApp Verification
          </Heading>
          <Paragraph className="text-sm text-gray-600">
            {codeSent
              ? `Enter the code sent to your WhatsApp number${whatsappNumber ? ` (${whatsappNumber})` : ''}`
              : `We will send a verification code to your registered WhatsApp number${whatsappNumber ? ` (${whatsappNumber})` : ''}`}
          </Paragraph>
        </div>

        {!codeSent && (
          <Button
            onClick={handleSendCode}
            className="w-full bg-primary text-white hover:bg-primary/90"
            size="lg"
            isLoading={isSettingUp}
          >
            Send Code
          </Button>
        )}

        {codeSent && (
          <>
            <div className="flex flex-col gap-3">
              <Label className="text-sm font-medium">
                Enter 6-digit code
                {error && (
                  <span className="text-red-500 text-xs ml-auto float-right">
                    {error}
                  </span>
                )}
              </Label>
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={(value) => {
                  setOtp(value);
                  setError('');
                }}
              >
                <InputOTPGroup className="gap-2 justify-center">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <InputOTPSlot
                      key={index}
                      index={index}
                      className={error ? 'border-red-500 bg-red-50' : ''}
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="flex justify-between items-center">
              <Button
                onClick={handleResend}
                disabled={!canResend || isSettingUp}
                variant="ghost"
                className="text-sm font-medium p-0 h-auto"
              >
                Resend code
              </Button>
              <span className="text-sm text-gray-600">{formatTime(timer)}</span>
            </div>
          </>
        )}
      </div>
    </TwoFactorDialogWrapper>
  );
};
